import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wrench } from "lucide-react";

export default function LoginPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      const dest = location.state?.from || (u.role === "admin" || u.role === "staff" ? "/admin" : "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2">
      <div className="bg-zinc-950/40 border-r border-zinc-900 p-10 lg:p-16 flex flex-col justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="h-9 w-9 bg-brand-red grid place-items-center"><Wrench className="h-5 w-5 text-black" strokeWidth={2.5} /></div>
          <div className="font-display uppercase text-xl text-white tracking-tight">Tassie Automotive</div>
        </Link>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Customer portal</div>
          <h1 className="mt-4 font-display uppercase text-5xl lg:text-6xl tracking-tighter text-white leading-[0.9]">
            Welcome back.<br /><span className="text-brand-red">Keys, please.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md">
            Sign in to track your service status, view past invoices, and book your next appointment in seconds.
          </p>
        </div>
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-600">Bay 03 — Diagnostics online</p>
      </div>

      <div className="p-10 lg:p-16 flex items-center">
        <form data-testid="login-form" onSubmit={submit} className="w-full max-w-md space-y-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brand-red">Step 01</div>
            <h2 className="mt-2 font-display uppercase text-3xl text-white tracking-tight">Sign in</h2>
          </div>

          <Field label="Email or username">
            <input data-testid="login-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ta-input" placeholder="you@example.com" />
          </Field>
          <Field label="Password">
            <input data-testid="login-password" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="ta-input" placeholder="••••••••" />
          </Field>

          {error && <div data-testid="login-error" className="border border-red-900 bg-red-950/40 text-red-300 text-sm px-4 py-3">{error}</div>}

          <button data-testid="login-submit" disabled={loading} className="w-full px-7 py-4 bg-brand-red text-white font-bold uppercase tracking-wider hover:bg-[#B91C2B] disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <p className="text-sm text-zinc-400">
            No account yet?{" "}
            <Link data-testid="login-to-register" to="/register" className="text-brand-red hover:underline">Create one</Link>
          </p>
        </form>
      </div>

      <style>{`
        .ta-input { width:100%; background:#0a0a0a; border:1px solid #27272a; color:#fafafa; padding:12px 14px; font-family:'IBM Plex Sans',sans-serif; font-size:14px; outline:none; transition:border-color .15s; }
        .ta-input:focus { border-color:#E11D2B; box-shadow:0 0 0 1px #E11D2B; }
        .ta-input::placeholder { color:#52525b; }
      `}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);
